import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';

import { API_URL } from '../../../api/api';

const AddProductLoanLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const [form, setForm] = useState({
        branch_id: '',
        group_id: '',
        member_id: '',
        unit: 1,
        product_id: '',
        open_date: '',
        guarantor2_id: '',
        staff_id: '',
        membernames: '',
        loan_amount: '',
        coapplicant_id: '',
        guarantor1_id: '',
        tenure: '',
        collection_type: 'Weekly',
        description: '',
        close_day: '',
        status: 'active',
        emi:'',
    });

    const [branches, setBranches] = useState([]);
    const [groups, setGroups] = useState([]);
    const [members, setMembers] = useState([]);
    const [products, setProducts] = useState([]);
    const [staffs, setStaffs] = useState([]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (id) {
            fetchProductLoan();
        }
    }, [id]);

       useEffect(() => {
            const loan_amount = parseFloat(form.loan_amount) || 0;
            const tenure = parseInt(form.tenure) || 0;

            if (loan_amount > 0 && tenure > 0) {
                const calculatedEMI = loan_amount / tenure;
                setForm(prev => ({ ...prev, emi: calculatedEMI.toFixed(2) }));
            } else {
                setForm(prev => ({ ...prev, emi: '' }));
            }
        }, [form.loan_amount, form.tenure]);

    const fetchInitialData = async () => {
        try {
            const [branchRes, productRes, staffRes] = await Promise.all([
                axios.get(`${API_URL}/branches`),
                axios.get(`${API_URL}/products`),
                axios.get(`${API_URL}/employees`)
            ]);
            setBranches(branchRes.data.data ?? branchRes.data);
            setProducts(productRes.data.data ?? productRes.data);
            setStaffs(staffRes.data.data ?? staffRes.data);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to load initial dropdown data", "error");
        }
    };


    const fetchProductLoan = async () => {
        try {
            const res = await axios.get(`${API_URL}/product-loans/${id}`);
            const loan = res.data;

            setForm({
                branch_id: loan.branch_id || '',
                group_id: loan.group_id || '',
                member_id: loan.member_id || '',
                unit: 1,
                product_id: loan.product_id || '',
                open_date: loan.open_date || '',
                guarantor2_id: loan.guarantor2_id || '',
                staff_id: loan.staff_id || '',
                membernames: '', // You can customize fetching member full name if needed
                loan_amount: loan.loan_amount || '',
                coapplicant_id: loan.coapplicant_id || '',
                guarantor1_id: loan.guarantor1_id || '',
                tenure: loan.tenure || '',
                collection_type: loan.collection_type || 'Weekly',
                description: loan.description || '',
                close_day: loan.close_day || '',
                status: loan.status || 'active',
                emi:form.emi,
            });

            if (loan.branch_id) {
                const groupsRes = await axios.get(`${API_URL}/branches/${loan.branch_id}/groups`);
                setGroups(groupsRes.data);
            }
            if (loan.group_id) {
                const membersRes = await axios.get(`${API_URL}/groups/${loan.group_id}/members`);
                setMembers(membersRes.data);
            }
        } catch (error) {
            console.error('Failed to load product loan', error);
            Swal.fire("Error", "Failed to load product loan data", "error");
        }
    };

    const handleBranchChange = async (option) => {
        setForm({ ...form, branch_id: option?.value ?? '', group_id: '', member_id: '', membernames: '' });
        if (option?.value) {
            try {
                const res = await axios.get(`${API_URL}/branches/${option.value}/groups`);
                setGroups(res.data);
                setMembers([]);
            } catch (error) {
                console.error("Error loading groups:", error);
            }
        }
    };

    const handleGroupChange = async (option) => {
        setForm({ ...form, group_id: option?.value ?? '', member_id: '', membernames: '' });
        if (option?.value) {
            try {
                const res = await axios.get(`${API_URL}/groups/${option.value}/members`);
                setMembers(res.data);
            } catch (error) {
                console.error("Error loading members:", error);
            }
        }
    };

    const handleMemberChange = (option) => {
        const memberId = option?.value ?? '';
        const selectedMember = members.find(m => m.id === memberId);
        const fullName = selectedMember ? `${selectedMember.firstname} ${selectedMember.lastname}` : '';
        setForm({ ...form, member_id: memberId, membernames: fullName });
    };

    const handleSelectChange = (field) => (option) => {
        setForm({ ...form, [field]: option?.value ?? '' });
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const val = type === 'radio' ? value : value;

        if (name === 'product_id') {
            const selectedProduct = products.find(p => p.id.toString() === value.toString());
            setForm(prev => ({
                ...prev,
                [name]: value,
                loan_amount: selectedProduct ? selectedProduct.sale_price : ''
            }));
        } else {
            setForm(prev => ({
                ...prev,
                [name]: val
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (id) {
                // Update existing product loan
                await axios.put(`${API_URL}/product-loans/${id}`, form);
                Swal.fire("Success", "Product Loan updated successfully", "success");
            } else {
                // Create new product loan
                await axios.post(`${API_URL}/product-loans`, form);
                Swal.fire("Success", "Product Loan created successfully", "success");
            }
            navigate('/manage-loan-search');
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Something went wrong", "error");
        }
    };

    // Helper: convert array to react-select format
    const toSelectOptions = (arr, labelKey = 'branchname') =>
        arr.map((item) => ({ value: item.id, label: item[labelKey] }));

    return (
        <form onSubmit={handleSubmit} className="p-4 bg-white rounded shadow-sm">
            <div className="row">
                {/* Left Column */}
                <div className="col-md-6">
                    {/* Branch */}
                    <div className="form-group mb-3">
                        <label>Branch</label>
                        <Select
                            options={toSelectOptions(branches, 'branchname')}
                            value={toSelectOptions(branches, 'branchname').find(opt => opt.value === form.branch_id)}
                            onChange={handleBranchChange}
                            placeholder="Select Branch"
                        />
                    </div>

                    {/* Group */}
                    <div className="form-group mb-3">
                        <label>Group</label>
                        <Select
                            options={toSelectOptions(groups, 'groupname')}
                            value={toSelectOptions(groups, 'groupname').find(opt => opt.value === form.group_id)}
                            onChange={handleGroupChange}
                            placeholder="Select Group"
                        />
                    </div>

                    {/* Member */}
                    <div className="form-group mb-3">
                        <label>Member</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.member_id)}
                            onChange={handleMemberChange}
                            placeholder="Select Member"
                        />
                    </div>


                    <div className="form-group mb-3">
                            <label>EMI</label>
                            <input
                                type="number"
                                className="form-control"
                                name="emi"
                                value={form.emi}
                                onChange={handleChange}
                                readOnly // Optional: prevent user edits if EMI is auto-calculated
                            />
                        </div>
                    {/* Unit */}
                    <div className="form-group mb-3">
                        <label>Unit</label>
                        <input type="number" className="form-control" name="unit" value={form.unit} onChange={handleChange} />
                    </div>

                    {/* Product */}
                    <div className="form-group mb-3">
                        <label>Product</label>
                        <Select
                            options={products.map(product => ({ value: product.id, label: product.name }))}
                            value={products
                                .map(product => ({ value: product.id, label: product.name }))
                                .find(opt => opt.value === form.product_id)}
                            onChange={(option) => {
                                const selectedProduct = products.find(p => p.id === option?.value);
                                setForm(prev => ({
                                    ...prev,
                                    product_id: option?.value || '',
                                    loan_amount: selectedProduct ? selectedProduct.sale_price : ''
                                }));
                            }}
                            placeholder="Select Product"
                        />
                    </div>

                    {/* Open Date */}
                    <div className="form-group mb-3">
                        <label>Open Date</label>
                        <input type="date" className="form-control" name="open_date" value={form.open_date} onChange={handleChange} />
                    </div>

                    {id && (
                        <>
                            {/* Close Date */}
                            <div className="form-group mb-3">
                                <label>Close Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="close_day"
                                    value={form.close_day}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Status */}
                            <div className="form-group mb-3">
                                <label>Status</label>
                                <select
                                    className="form-control"
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="active">Active</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>
                        </>
                    )}

                </div>

                {/* Right Column */}
                <div className="col-md-6">
                    {/* Staff */}
                    <div className="form-group mb-3">
                        <label>Staff</label>
                        <Select
                            options={toSelectOptions(staffs, 'firstname')}
                            value={toSelectOptions(staffs, 'firstname').find(opt => opt.value === form.staff_id)}
                            onChange={handleSelectChange('staff_id')}
                            placeholder="Select Staff"
                        />
                    </div>

                    {/* Member Name */}
                    <div className="form-group mb-3">
                        <label>Member Name</label>
                        <input type="text" className="form-control" value={form.membernames} readOnly />
                    </div>

                    {/* Loan Amount */}
                    <div className="form-group mb-3">
                        <label>Loan Amount</label>
                        <input type="number" className="form-control" name="loan_amount" value={form.loan_amount} onChange={handleChange} />
                    </div>

                    {/* Co-Applicant */}
                    <div className="form-group mb-3">
                        <label>Co-Applicant</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.coapplicant_id)}
                            onChange={handleSelectChange('coapplicant_id')}
                            placeholder="Select Co-Applicant"
                        />
                    </div>

                    {/* Guarantor 1 */}
                    <div className="form-group mb-3">
                        <label>Guarantor 1</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.guarantor1_id)}
                            onChange={handleSelectChange('guarantor1_id')}
                            placeholder="Select Guarantor 1"
                        />
                    </div>

                    {/* Guarantor 2 */}
                    <div className="form-group mb-3">
                        <label>Guarantor 2</label>
                        <Select
                            options={toSelectOptions(members, 'firstname')}
                            value={toSelectOptions(members, 'firstname').find(opt => opt.value === form.guarantor2_id)}
                            onChange={handleSelectChange('guarantor2_id')}
                            placeholder="Select Guarantor 2"
                        />
                    </div>

                    {/* Tenure */}
                    <div className="form-group mb-3">
                        <label>Tenure (weeks)</label>
                        <input type="number" className="form-control" name="tenure" value={form.tenure} onChange={handleChange} />
                    </div>

                    {/* Collection Type */}
                    <div className="form-group mb-3">
                        <label>Collection Type</label><br />
                        {['Daily', 'Weekly', 'Monthly'].map(type => (
                            <div key={type} className="form-check checked-secondary d-flex align-items-center gap-2 me-3" style={{ display: 'inline-flex' }}>
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="collection_type"
                                    id={`collection_type-${type}`}
                                    value={type}
                                    checked={form.collection_type === type}
                                    onChange={handleChange}
                                />
                                <label
                                    className="form-check-label line-height-1 fw-medium text-secondary-light"
                                    htmlFor={`collection_type-${type}`}
                                    style={{ marginBottom: 0 }}
                                >
                                    {type}
                                </label>
                            </div>
                        ))}
                    </div>

                    {/* Description */}
                    <div className="form-group mb-3">
                        <label>Description</label>
                        <textarea className="form-control" name="description" value={form.description} onChange={handleChange}></textarea>
                    </div>
                </div>
            </div>

            {/* Submit Button */}
            <div className="text-center mt-3">
                <button type="submit" className="btn btn-primary px-4">{id ? 'Update' : 'Apply'}</button>
            </div>
        </form>
    );
};

export default AddProductLoanLayer;
